import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ListContextMenuIconButton } from "../../components/styled";
import { routes } from "../../config/routes";
import { useSelector } from "../../store/hooks";
import {
  notifyRecommendation,
  setChangeReportDialog,
  setCopyDialog,
  setDeleteDialog,
} from "../../store/recommendations-page/recommendationsPage";
import { selectCanNotify } from "../../store/recommendations-page/selectors";
import { AppDispatch } from "../../store/store";
import { RecommendationAzure } from "../../types/types";
import { RecommendationSummaryPanelContext } from "./RecommendationSummaryPanelContext";

type Props = {
  rec: RecommendationAzure;
};

export const ContextButton = ({ rec }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canNotify = useSelector(selectCanNotify)(rec);
  const dispatch: AppDispatch = useDispatch();
  const isNotifyLoading = useSelector(
    (s) => s.recommendationsPage.isNotifyLoading,
  );
  const userRole = useSelector((s) => s.userRole.userRole);
  const onShowSummary = useContext(RecommendationSummaryPanelContext);

  const onClickEditRecommendation = () => {
    navigate(
      routes.editRecommendation.getPath(
        rec.webBasePartnerNumber,
        rec.locationId,
        rec.reportId,
        rec.recommendationId,
      ) + `?tab=recommendation`,
    );
  };

  const onClickShowResponses = () => {
    navigate(
      routes.editRecommendation.getPath(
        rec.webBasePartnerNumber,
        rec.locationId,
        rec.reportId,
        rec.recommendationId,
      ) + `?tab=responses`,
    );
  };

  const onClickNotify = () => {
    dispatch(notifyRecommendation.trigger(rec));
  };

  const onClickDelete = () => {
    dispatch(
      setDeleteDialog({
        recommendation: rec,
        isOpen: true,
      }),
    );
  };

  const onClickCopy = () => {
    dispatch(
      setCopyDialog({
        recommendation: rec,
        isOpen: true,
      }),
    );
  };

  const onClickChangeReport = () => {
    dispatch(
      setChangeReportDialog({
        recommendation: rec,
        isOpen: true,
      }),
    );
  };

  const isNotifyButtonLoading = isNotifyLoading || canNotify.isLoading;
  const isNotifyButtonDisabled = !canNotify.result || isNotifyButtonLoading;

  const items = [
    {
      key: "responses",
      text: t("ram.recommendationList.addResponse"),
      iconProps: { iconName: "Comment" },
      onClick: onClickShowResponses,
    },
  ];

  const editRecommendationObj = {
    key: "editRecommendation",
    text: t("ram.recommendationList.editRecommendation"),
    iconProps: { iconName: "Edit" },
    onClick: onClickEditRecommendation,
    disabled: false, //!isWaitingForGREG && !isClosed,
  };
  const showSummaryObj = {
    key: "showSummary",
    text: t("ram.showSummary.label"),
    iconProps: { iconName: "Info" },
    onClick: () => onShowSummary?.(rec),
  };
  const notifyObj = {
    key: "notify",
    text: t("greco.notify"),
    iconProps: {
      iconName: isNotifyButtonLoading ? "ProgressRingDots" : "Ringer",
    },
    onClick: onClickNotify,
    disabled: isNotifyButtonDisabled,
  };

  const deleteOption = {
    key: "delete",
    text: t("greco.delete"),
    iconProps: {
      iconName: "Trash",
    },
    onClick: onClickDelete,
  };

  const changeReportOption = {
    key: "changeReport",
    text: t("ram.changeReport.label"),
    iconProps: {
      iconName: "Switch",
    },
    onClick: onClickChangeReport,
  };

  if (
    userRole === "admin" ||
    userRole === "internal" ||
    userRole === "internal_role" ||
    userRole === "external_advanced"
  ) {
    items.push(editRecommendationObj);
  }

  if (
    userRole === "admin" ||
    userRole === "internal" ||
    userRole === "internal_role" ||
    userRole === "external" ||
    userRole === "external_advanced"
  ) {
    items.push(showSummaryObj);
  }

  if (
    userRole === "admin" ||
    userRole === "internal" ||
    userRole === "internal_role" ||
    userRole === "external_advanced"
  ) {
    items.push(notifyObj);
  }

  if (userRole === "admin") {
    items.push(changeReportOption);
    items.push(deleteOption);
  }

  const copyOption = {
    key: "copy",
    text: t("ram.recommendationList.copyRecommendation"),
    iconProps: {
      iconName: "Copy",
    },
    onClick: onClickCopy,
  };

  if (
    userRole === "admin" ||
    userRole === "internal" ||
    userRole === "internal_role" ||
    userRole === "external_advanced"
  ) {
    items.push(copyOption);
  }

  return (
    <ListContextMenuIconButton
      iconProps={{ iconName: "MoreVertical" }}
      menuIconProps={{ style: { display: "none" } }}
      menuProps={{
        items: items,
      }}
    />
  );
};
